import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface CardProps extends ComponentPropsWithRef<'section'> {
  /** Без внутрішніх відступів (таблиця на всю ширину картки). */
  flush?: boolean;
}

/** Картка: поверхня з рамкою, радіус 16 (D33). */
export function Card({ flush, className, children, ...rest }: CardProps) {
  return (
    <section className={cn('rounded-card border border-border bg-surface', !flush && 'p-4 sm:p-5', className)} {...rest}>
      {children}
    </section>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ title, description, actions, className }: CardHeaderProps) {
  return (
    <div className={cn('mb-3 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {description ? <p className="mt-0.5 text-[13px] text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
  /** На всю ширину (довгий текст). */
  wide?: boolean;
  hint?: ReactNode;
}

export interface KeyValueProps {
  items: readonly KeyValueItem[];
  /** Колонки на широкому екрані. */
  columns?: 1 | 2 | 3;
  className?: string;
}

/** Список «підпис — значення» (картка запису, панель деталей). */
export function KeyValue({ items, columns = 2, className }: KeyValueProps) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-3',
        columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((it, i) => (
        <div key={i} className={cn('min-w-0', it.wide && 'sm:col-span-full')}>
          <dt className="text-[12px] leading-4 font-semibold text-muted">{it.label}</dt>
          <dd className="mt-0.5 text-[14px] break-words">{it.value === null || it.value === undefined || it.value === '' ? <span className="text-faint">—</span> : it.value}</dd>
          {it.hint ? <p className="mt-0.5 text-[12px] text-faint">{it.hint}</p> : null}
        </div>
      ))}
    </dl>
  );
}
